import { useEffect, useRef, useState, useCallback } from "react";
import { supabase } from "../lib/supabase";
import {
  getCached,
  setCached,
  invalidateCache,
  CACHE_KEYS,
} from "../utils/sessionCache";
import { getTnStatus, connectTn } from "../lib/tiendaNube";
import { loadOnboardingSeen, isSeen, markSeen } from "../lib/onboardingSeen";
import { fetchHotSaleCoupon } from "../lib/hotSaleCoupon";

/** Cuál de los banners promocionales de home corresponde mostrar (o ninguno). */
export type HomeBannerKind =
  | "upsell"
  | "brand"
  | "cuestionario"
  | "tiendanube"
  | null;

/** Estado de la suscripción que amerita un banner informativo en la home. */
export type SubscriptionStatus = "cancelled" | "paused" | null;

export type HomeBannersState = {
  /**
   *  - "upsell": usuario de prueba gratis que ya generó ≥5 imágenes.
   *  - "brand": usuario pago sin análisis de marca (`config.media_agent_analysis`).
   *  - "cuestionario": usuario pago que todavía no respondió la encuesta de
   *    feedback (`company_profiles.cuestionario_respondido`).
   *  - "tiendanube": usuario pago CON análisis hecho, sin Tienda Nube conectada
   *    y que no descartó el banner (post "Mejorá tus fotos").
   *  - null: no corresponde ninguno (o aún cargando).
   */
  kind: HomeBannerKind;
  /**
   * Saldo de créditos (`imagenes_restantes`). Se expone para **todos** los
   * planes, no sólo el gratis: el aviso de "sin créditos" / "se te están por
   * acabar" vale para cualquiera (ADR-2026-07-30-solo-criterio-saldo-toda-app).
   */
  freeImagesRemaining: number | null;
  /**
   * Capacidad del plan (`max_fotos_mes`), el denominador del umbral del 5%. Ver
   * `lib/creditsStatus`.
   */
  creditsTotal: number | null;
  /** Empresa del usuario — la necesita el modal del cuestionario. */
  idCompany: string | null;
  /**
   * País del usuario === Argentina. Ya NO cambia el copy del upsell (ADR-2026-07-30-banner-home-habla-descuento-real:
   * el banner habla del descuento real, que es el mismo para todos); queda
   * porque es contexto barato de tener resuelto.
   */
  isArgentina: boolean;
  /**
   * Descuento vigente en % entero (ej. `50`), del mismo cupón invisible que
   * aplica el catálogo (`lib/hotSaleCoupon`). `null` si no hay cupón activo — y
   * entonces el banner NO promete ningún descuento. Solo se resuelve para
   * "upsell". Ver ADR-2026-07-30-banner-home-habla-descuento-real.
   */
  discountPercent: number | null;
  /**
   * Estado de la suscripción (INDEPENDIENTE de `kind`):
   *  - "cancelled": hay una cancelación programada a futuro.
   *  - "paused": la suscripción está pausada (reanuda a futuro).
   *  - null: ninguno.
   */
  status: SubscriptionStatus;
  /** Fecha de cancelación formateada (solo si status === "cancelled"). */
  cancelDate: string | null;
  /** Fecha de reanudación formateada (solo si status === "paused"). */
  resumeDate: string | null;
  /**
   * Motivo de la cancelación (`company_profiles.trigger_cancelacion`). Solo
   * relevante si status === "cancelled". Valor conocido: "fondos_insuficientes"
   * (el último pago fue rechazado por falta de fondos) → la UI muestra un copy
   * distinto (por rechazo de pago, con reintento automático). `null` = baja
   * voluntaria u otro motivo (copy default).
   */
  cancelTrigger: string | null;
  /** Descarta el banner de Tienda Nube ("No me interesa") — por usuario, en la DB
   *  (`onboarding_seen`). Lo inyecta el hook; el render lo llama desde el botón. */
  dismissTiendanube?: () => void;
  /** Conecta Tiendanube (OAuth popup) sobre la marca default y oculta el banner
   *  al éxito. Lanza si el user cancela / popup bloqueado (el banner queda). */
  connectTiendanube?: () => Promise<void>;
  /** Oculta el banner del cuestionario tras responderlo. El flag real
   *  (`cuestionario_respondido`) lo escribe el webhook de premio, así que acá
   *  solo actualizamos el estado local para que el banner se vaya en el acto. */
  dismissCuestionario?: () => void;
};

/**
 * Cuántas imágenes tiene que haber generado un usuario de prueba gratis para que le
 * aparezca el banner de "suscribite a un plan". Bajó de 5 a 3 el 2026-08-03
 * (ADR-2026-08-03-upsell-de-home-a-las-3-imagenes): con 5 el aviso llegaba tarde —
 * la prueba gratis da 10 créditos, así que recién aparecía con la mitad ya gastada.
 *
 * Se cuenta con el MISMO criterio que el resto de la app: filas de
 * `imagenes_generadas` sin error y con `creditos_gastados > 0` (ver el query).
 */
const MIN_IMAGENES_UPSELL = 3;

const EMPTY: HomeBannersState = {
  kind: null,
  freeImagesRemaining: null,
  creditsTotal: null,
  idCompany: null,
  isArgentina: false,
  discountPercent: null,
  status: null,
  cancelDate: null,
  resumeDate: null,
  cancelTrigger: null,
};

// Mismo formato que el Paywall ("20 de julio de 2026").
const formatDate = (raw: unknown): string | null => {
  if (!raw) return null;
  const d = new Date(raw as string);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString("es-AR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

/**
 * Capacidad por plan para el fallback de dev (ADR-2026-07-30-panel-dev-plan-creditos-generadas y ADR-2026-07-30-solo-criterio-saldo-toda-app): sin empresa no hay
 * `max_fotos_mes` que leer, y sin denominador el umbral del 5% no se puede
 * calcular. Son los valores del catálogo real.
 */
const isFuture = (raw: unknown): boolean => {
  if (!raw) return false;
  const d = new Date(raw as string);
  return !isNaN(d.getTime()) && d.getTime() > Date.now();
};

/**
 * Resuelve qué banner de home mostrar, con las MISMAS condiciones que la
 * pantalla clásica de selección de productos. Fuente única para que el clásico
 * y la Interfaz V2 muestren exactamente los mismos banners.
 *
 * Banners promocionales (`kind`, mutuamente excluyentes). El orden de esta
 * lista ES la prioridad: el primero que aplica gana y corta la evaluación.
 *  - Plan gratis + ≥`MIN_IMAGENES_UPSELL` imágenes generadas → "upsell" (prueba gratis).
 *  - Plan pago + sin `media_agent_analysis` → "brand" (describir la marca),
 *    salvo que `suppressBrand` esté activo.
 *  - Plan pago + encuesta sin responder → "cuestionario". Va después de
 *    "brand" porque describir la marca mejora lo que la IA genera (más
 *    urgente para el usuario) y antes de "tiendanube", que es el menos
 *    apremiante de los tres. Los tres son una SECUENCIA: al completar uno
 *    aparece el siguiente (ADR-889).
 *
 * Estado de suscripción (`status`, ortogonal a `kind`):
 *  - Cancelación programada a futuro → "cancelled".
 *  - Reanudación programada a futuro (pausa) → "paused".
 * El consumidor decide si renderizar el banner de estado (ver `HomeBannersView`
 * con `showStatus`); hoy solo se muestra en mobile.
 *
 * @param opts.suppressBrand fuerza ocultar el banner de marca (ej. al volver de
 *   `/redes-sociales/descripcion-brand` con `hideBrandBanner`).
 */
export function useHomeBanners(opts?: {
  suppressBrand?: boolean;
}): HomeBannersState {
  const suppressBrand = opts?.suppressBrand ?? false;
  const [state, setState] = useState<HomeBannersState>(EMPTY);
  const uidRef = useRef<string | null>(null);
  const companyRef = useRef<string | null>(null);
  /**
   * Contador para reevaluar la cadena de banners sin recargar la página
   * (ADR-889). Lo bumpea `dismissCuestionario`: al responder la encuesta, el
   * paso siguiente —Tienda Nube— tiene que aparecer ahí mismo, porque es el
   * final de una secuencia y no un banner suelto.
   */
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    let cancelled = false;
    // Sin usuario o sin empresa el hook no tiene nada que leer y se va sin pintar banner.
    (async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) return;
        uidRef.current = user.id;

        const { data: profile } = await supabase
          .from("profiles")
          .select("id_company")
          .eq("id", user.id)
          .single();
        const idCompany = profile?.id_company as string | undefined;
        if (!idCompany) return;
        companyRef.current = idCompany;

        const { data: company } = await supabase
          .from("company_profiles")
          .select(
            // `created_at` salió del select junto con el requisito de antigüedad
            // de la encuesta (ADR-889): ya no lo mira nadie.
            "plan, pais, imagenes_restantes, max_fotos_mes, fecha_cancelacion_programada, fecha_reactivacion_programada, trigger_cancelacion, cuestionario_respondido",
          )
          .eq("id_company", idCompany)
          .single();
        if (cancelled) return;

        const plan = ((company as any)?.plan || "gratis") as string;
        const pais = ((company as any)?.pais || "") as string;
        const imagenesRestantes =
          typeof (company as any)?.imagenes_restantes === "number"
            ? ((company as any).imagenes_restantes as number)
            : null;
        // Denominador del umbral del 5% (ADR-2026-07-30-solo-criterio-saldo-toda-app).
        const capacidadPlan =
          typeof (company as any)?.max_fotos_mes === "number"
            ? ((company as any).max_fotos_mes as number)
            : null;
        const isArgentina = pais.toLowerCase() === "argentina";
        const isPaidPlan = plan && plan !== "gratis";

        // Estado de la suscripción (ortogonal al banner promocional). Solo
        // aplica a usuarios pagos; un free no tiene cancelación/pausa.
        const cancelRaw = (company as any)?.fecha_cancelacion_programada;
        const resumeRaw = (company as any)?.fecha_reactivacion_programada;
        const triggerRaw = (company as any)?.trigger_cancelacion;
        let status: SubscriptionStatus = null;
        let cancelDate: string | null = null;
        let resumeDate: string | null = null;
        let cancelTrigger: string | null = null;
        if (isPaidPlan && isFuture(cancelRaw)) {
          status = "cancelled";
          cancelDate = formatDate(cancelRaw);
          cancelTrigger = (triggerRaw as string) || null;
        } else if (isPaidPlan && isFuture(resumeRaw)) {
          status = "paused";
          resumeDate = formatDate(resumeRaw);
        }

        if (!isPaidPlan) {
          // Prueba gratis: upsell tras `MIN_IMAGENES_UPSELL` imágenes generadas.
          // `creditos_gastados > 0` = lo que el usuario generó Y pagó: incluye las
          // que después borró (las generó igual) y excluye las intermedias del
          // multipose, que sin este filtro adelantaban el upsell. Ver ADR-680.
          //
          // `or("has_error.is.null,has_error.eq.false")` y no `neq("has_error", true)`:
          // en SQL `has_error <> true` es NULL cuando la columna es NULL, así que esas
          // filas quedaban FUERA del conteo — descartar como "con error" algo que sólo
          // es "sin dato". Hoy no hay NULLs (0 en 30 días), pero el filtro estaba
          // contando de menos por accidente, no por decisión.
          const { count } = await supabase
            .from("imagenes_generadas")
            .select("id", { count: "exact", head: true })
            .or("has_error.is.null,has_error.eq.false")
            .gt("creditos_gastados", 0)
            .eq("id_company", idCompany);
          if (cancelled) return;
          const esUpsell = (count ?? 0) >= MIN_IMAGENES_UPSELL;
          // El descuento se consulta SOLO si el banner va a mostrarse, y sale del
          // mismo cupón que aplica el catálogo: si acá dijéramos un % fijo y el
          // cupón estuviera vencido o con otro multiplicador, el banner prometería
          // un precio que el checkout no cobra (ADR-2026-07-30-banner-home-habla-descuento-real).
          const cupon = esUpsell ? await fetchHotSaleCoupon() : null;
          if (cancelled) return;
          setState({
            kind: esUpsell ? "upsell" : null,
            freeImagesRemaining: imagenesRestantes,
            creditsTotal: capacidadPlan,
            idCompany,
            isArgentina,
            discountPercent: cupon
              ? Math.round((1 - cupon.multiplicador) * 100)
              : null,
            status,
            cancelDate,
            resumeDate,
            cancelTrigger,
          });
          return;
        }

        // Plan pago: banner de marca si todavía no hay análisis de marca.
        if (suppressBrand) {
          setState({
            kind: null,
            freeImagesRemaining: imagenesRestantes,
            creditsTotal: capacidadPlan,
            idCompany,
            isArgentina,
            discountPercent: null,
            status,
            cancelDate,
            resumeDate,
            cancelTrigger,
          });
          return;
        }
        let hasAnalysis = getCached<boolean>(CACHE_KEYS.COMPANY_CONFIG);
        if (hasAnalysis === null) {
          try {
            const { data: config } = await supabase
              .from("config")
              .select("media_agent_analysis")
              .eq("id_company", idCompany)
              .maybeSingle();
            let computed = !!config?.media_agent_analysis;
            // Fallback robusto: `media_agent_analysis` puede quedar VACÍO ('')
            // aunque el user YA haya completado "Mejorá tus fotos" (el webhook
            // del análisis a veces devuelve vacío). En ese caso igual ocultamos
            // el banner si la marca DEFAULT ya tiene IDENTIDAD cargada (colores
            // o contexto) — así el banner es one-time real: desaparece cuando
            // la marca ya está completada, sin depender de ese flag poco fiable.
            if (!computed) {
              const { data: brand } = await supabase
                .from("brand_profiles")
                .select("colors, context")
                .eq("id_company", idCompany)
                .eq("is_default", true)
                .eq("is_draft", false)
                .limit(1)
                .maybeSingle();
              const colors = (brand as { colors?: unknown } | null)?.colors;
              const ctx = (brand as { context?: unknown } | null)?.context;
              const hasIdentity =
                (Array.isArray(colors) && colors.length > 0) ||
                (typeof ctx === "string" && ctx.trim().length > 0);
              if (hasIdentity) computed = true;
            }
            hasAnalysis = computed;
            setCached(CACHE_KEYS.COMPANY_CONFIG, hasAnalysis);
          } catch {
            hasAnalysis = false;
          }
        }
        if (cancelled) return;
        if (!hasAnalysis) {
          setState({
            kind: "brand",
            freeImagesRemaining: imagenesRestantes,
            creditsTotal: capacidadPlan,
            idCompany,
            isArgentina,
            discountPercent: null,
            status,
            cancelDate,
            resumeDate,
            cancelTrigger,
          });
          return;
        }

        // Cuestionario (encuesta de feedback, +10 imágenes de premio): plan
        // pago + todavía sin responder. Antes esta condición vivía suelta en
        // `SeleccionarProductosAnuncio` y su banner se renderizaba ENCIMA de
        // este componente, así que podía salir junto con el de marca o el de
        // Tienda Nube. Al ser un `kind` más, la exclusión mutua es
        // estructural. Ver ADR-681.
        //
        // Ya NO se pide antigüedad de la empresa. Pedir ≥2 semanas partía la
        // secuencia justo para quien recién se suscribe: completaba la marca y
        // el paso siguiente se salteaba, cayendo directo en Tienda Nube; la
        // encuesta recién aparecía dos semanas después, fuera de contexto. El
        // orden pedido es marca → encuesta → Tienda Nube, uno detrás del otro
        // (ADR-889).
        const cuestionarioRespondido =
          (company as any)?.cuestionario_respondido === true;
        if (!cuestionarioRespondido) {
          setState({
            kind: "cuestionario",
            freeImagesRemaining: imagenesRestantes,
            creditsTotal: capacidadPlan,
            idCompany,
            isArgentina,
            discountPercent: null,
            status,
            cancelDate,
            resumeDate,
            cancelTrigger,
          });
          return;
        }

        // Análisis de marca YA hecho → evaluar el banner de Tienda Nube (aparece
        // justo donde antes no quedaba nada, post "Mejorá tus fotos"). Se muestra
        // si el user NO lo descartó ("No me interesa", por usuario) y la empresa
        // NO tiene ninguna tienda conectada.
        await loadOnboardingSeen(user.id);
        if (cancelled) return;
        if (isSeen("tiendanube_banner")) {
          setState({
            kind: null,
            freeImagesRemaining: imagenesRestantes,
            creditsTotal: capacidadPlan,
            idCompany,
            isArgentina,
            discountPercent: null,
            status,
            cancelDate,
            resumeDate,
            cancelTrigger,
          });
          return;
        }

        let tnConnected = getCached<boolean>(CACHE_KEYS.TN_STATUS);
        if (tnConnected === null) {
          try {
            const { data: brandRow } = await supabase
              .from("brand_profiles")
              .select("id")
              .eq("id_company", idCompany)
              .eq("is_default", true)
              .eq("is_draft", false)
              .limit(1)
              .maybeSingle();
            const bId = (brandRow as { id?: string } | null)?.id ?? null;
            const st = await getTnStatus(bId);
            // `companyHasStores`: si CUALQUIER marca de la empresa ya tiene tienda
            // conectada, no ofrecemos conectar (es por empresa, no por marca).
            tnConnected = st.connected || st.companyHasStores;
            setCached(CACHE_KEYS.TN_STATUS, tnConnected);
          } catch {
            tnConnected = false;
          }
        }
        if (cancelled) return;
        if (tnConnected) {
          // Ya conectó una tienda → marcamos el flag "resuelto" (mismo que
          // "No me interesa") para que el banner NO vuelva nunca, ni si después
          // desconecta. En la próxima carga `isSeen` corta antes (sin consultar
          // el estado de conexión).
          markSeen("tiendanube_banner", uidRef.current);
        }
        setState({
          kind: tnConnected ? null : "tiendanube",
          freeImagesRemaining: imagenesRestantes,
          creditsTotal: capacidadPlan,
          idCompany,
          isArgentina,
          discountPercent: null,
          status,
          cancelDate,
          resumeDate,
          cancelTrigger,
        });
      } catch {
        // Si la consulta falla (sesión vencida, permisos) la home queda sin banner.
        if (!cancelled) setState(EMPTY);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [suppressBrand, revision]);

  // "No me interesa": marca el flag por usuario (DB) y oculta el banner ya. En la
  // próxima carga, `isSeen("tiendanube_banner")` lo mantiene oculto.
  const dismissTiendanube = useCallback(() => {
    markSeen("tiendanube_banner", uidRef.current);
    setState((s) => ({ ...s, kind: null }));
  }, []);

  // "Conectar": mismo flujo OAuth que Mi marca (`connectTn`, popup + espera),
  // sobre la marca DEFAULT (igual que el chequeo que decide mostrar el banner).
  // Al éxito: cache TN a "conectada", flag resuelto (el banner no vuelve) y
  // ocultar ya. Si lanza (cancelado / popup bloqueado), el banner queda.
  const connectTiendanube = useCallback(async () => {
    const idCompany = companyRef.current;
    if (!idCompany) throw new Error("Empresa no disponible.");
    const { data: brandRow } = await supabase
      .from("brand_profiles")
      .select("id")
      .eq("id_company", idCompany)
      .eq("is_default", true)
      .eq("is_draft", false)
      .limit(1)
      .maybeSingle();
    const bId = (brandRow as { id?: string } | null)?.id ?? null;
    await connectTn(bId);
    setCached(CACHE_KEYS.TN_STATUS, true);
    markSeen("tiendanube_banner", uidRef.current);
    setState((s) => ({ ...s, kind: null }));
  }, []);

  /**
   * Encuesta respondida. El webhook de premio marca `cuestionario_respondido`
   * en la DB; acá se baja el banner y se **reevalúa la cadena** para que el paso
   * siguiente (Tienda Nube) aparezca en el momento, sin esperar a la próxima
   * carga de la home (ADR-889).
   *
   * Antes sólo bajaba el banner, para ahorrarse la consulta al vuelo. El costo
   * de eso era romper la secuencia justo en su último tramo: el usuario
   * completaba la encuesta y no pasaba nada visible.
   *
   * Se invalida el cache de sesión del estado de Tienda Nube: sin eso la
   * reevaluación releería el mismo valor cacheado.
   */
  const dismissCuestionario = useCallback(() => {
    setState((s) => ({ ...s, kind: null }));
    invalidateCache(CACHE_KEYS.TN_STATUS);
    setRevision((r) => r + 1);
  }, []);

  return {
    ...state,
    dismissTiendanube,
    connectTiendanube,
    dismissCuestionario,
  };
}
